import React, { useRef, useCallback, useEffect } from "react"

/**
 * Resizable panel with a drag handle on its trailing edge (right for "row", bottom for "column").
 * Size is controlled: pass `size` (px, or null for `defaultSize`) and persist it via `onResize`.
 *
 * @param props {{
 *  children: React.ReactNode,
 *  size?: number | null,
 *  defaultSize?: number | string,
 *  minSize?: number,
 *  minRemaining?: number,
 *  direction?: "row" | "column",
 *  disabled?: boolean,
 *  onResize?: (size: number) => void,
 *  style?: React.CSSProperties,
 * }}
 */
export default function Resizable({
  children,
  size = null,
  defaultSize = "50%",
  minSize = 100,
  minRemaining = 100,
  direction = "row",
  disabled = false,
  onResize,
  style,
}) {
  const ref = useRef(null)
  const cleanupRef = useRef(null)
  const isColumn = direction === "column"

  useEffect(() => () => cleanupRef.current?.(), [])

  const startResize = useCallback(
    (e) => {
      const panel = ref.current
      const parent = panel?.parentElement
      if (!panel || !parent) return
      e.preventDefault()

      const start = isColumn ? e.clientY : e.clientX
      const startSize = isColumn ? panel.offsetHeight : panel.offsetWidth
      const parentSize = isColumn ? parent.offsetHeight : parent.offsetWidth
      const maxSize = Math.max(minSize, parentSize - minRemaining)

      const move = (ev) => {
        const delta = (isColumn ? ev.clientY : ev.clientX) - start
        const next = Math.min(maxSize, Math.max(minSize, startSize + delta))
        onResize?.(next)
      }
      const stop = () => {
        window.removeEventListener("mousemove", move)
        window.removeEventListener("mouseup", stop)
        document.body.style.userSelect = ""
        cleanupRef.current = null
      }

      document.body.style.userSelect = "none"
      window.addEventListener("mousemove", move)
      window.addEventListener("mouseup", stop)
      cleanupRef.current = stop
    },
    [isColumn, minSize, minRemaining, onResize],
  )

  if (disabled) {
    return <div style={{ position: "relative", ...style }}>{children}</div>
  }

  const current = size == null ? defaultSize : size
  const dim = isColumn ? "height" : "width"
  const minDim = isColumn ? "minHeight" : "minWidth"

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: isColumn ? "column" : "row",
        flex: "none",
        ...style,
        [dim]: current,
        [minDim]: minSize,
        maxWidth: isColumn ? undefined : "100%",
      }}
    >
      <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto" }}>
        {children}
      </div>
      <div
        onMouseDown={startResize}
        style={{
          cursor: isColumn ? "row-resize" : "col-resize",
          flex: "none",
          alignSelf: "stretch",
          height: isColumn ? 12 : undefined,
          width: isColumn ? undefined : 12,
          backgroundColor: "#e1e1e1",
          display: "flex",
          flexDirection: isColumn ? "row" : "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 4,
        }}
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              width: 4,
              height: 4,
              borderRadius: "50%",
              backgroundColor: "#8a8a8a",
              pointerEvents: "none",
            }}
          />
        ))}
      </div>
    </div>
  )
}
